import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Avatar
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import { getTenantConfig } from '../config/tenantConfig';
/**
 * DatasetList - Modern, utilitarian table view of datasets
 */
export default function DatasetList({
  datasets,
  onImport,
  onViewDetails,
  onDelete,
  onToggleStatus,
  onRebuild,
  onEdit
}) {
  function formatDate(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);

    // Format: "7 Jul 14:23"
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

    return `${day} ${month} ${time}`;
  }

  function getStatusColor(status) {
    return status === 'ACTIVE' ? 'success' : 'default';
  }

  function getImportStatus(dataset) {
    if (!dataset.import_history || dataset.import_history.length === 0) {
      return 'none';
    }
    const lastImport = dataset.import_history[dataset.import_history.length - 1];
    return lastImport.status === 'FAILED' ? 'failed' : 'success';
  }

  function getImportIconColor(dataset) {
    const status = getImportStatus(dataset);
    if (status === 'failed') return 'error.main';
    if (status === 'success') return 'success.main';
    return 'action.disabled';
  }

  function getImportIconHoverColor(dataset) {
    const status = getImportStatus(dataset);
    if (status === 'failed') return { bgcolor: 'error.50', color: 'error.main' };
    if (status === 'success') return { bgcolor: 'success.50', color: 'success.main' };
    return { bgcolor: 'action.hover', color: 'action.active' };
  }

  function getDetailsTooltip(dataset) {
    const status = getImportStatus(dataset);
    if (status === 'failed') return 'Details (Last import failed!)';
    if (status === 'success') return 'Details (Last import successful)';
    return 'Details (No imports yet)';
  }

  function getIconUrl(dataset) {
    // Return icon_url from top level (full URL), or null for placeholder
    return dataset.icon_url || null;
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1
      }}
    >
      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, fontSize: '0.8125rem' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 50 }}>
              Icon
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              ID
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Name
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Description
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 85 }}>
              Status
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              Count
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 100 }}>
              Last Import
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 100 }}>
              Created
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 145 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {datasets.map((dataset) => (
            <TableRow
              key={dataset.dataset_id}
              hover
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child td': { borderBottom: 0 }
              }}
            >
              <TableCell>
                <Avatar
                  src={getIconUrl(dataset)}
                  variant="rounded"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'grey.100'
                  }}
                >
                  {!getIconUrl(dataset) && (
                    <ImageIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  )}
                </Avatar>
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: 'primary.main'
                  }}
                >
                  {dataset.dataset_id}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                  {dataset.dataset_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    color: dataset.description ? 'text.secondary' : 'text.disabled',
                    fontStyle: dataset.description ? 'normal' : 'italic',
                    fontSize: '0.8125rem'
                  }}
                >
                  {dataset.description || 'No description'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={dataset.status}
                  color={getStatusColor(dataset.status)}
                  size="small"
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    height: 22
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 500,
                    fontSize: '0.8125rem'
                  }}
                >
                  {dataset.person_count || 0}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {formatDate(dataset.last_import)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {formatDate(dataset.created_at)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(dataset)}
                      sx={{
                        '&:hover': { bgcolor: 'info.50', color: 'info.main' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Import" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onImport(dataset)}
                      sx={{
                        '&:hover': { bgcolor: 'primary.50', color: 'primary.main' }
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Rebuild Collection" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onRebuild(dataset)}
                      sx={{
                        '&:hover': { bgcolor: 'warning.50', color: 'warning.main' }
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={getDetailsTooltip(dataset)} arrow>
                    <IconButton
                      size="small"
                      onClick={() => onViewDetails(dataset)}
                      sx={{
                        '&:hover': getImportIconHoverColor(dataset),
                        color: getImportIconColor(dataset)
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={dataset.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} arrow>
                    <IconButton
                      size="small"
                      onClick={() => onToggleStatus(dataset)}
                      sx={{
                        '&:hover': {
                          bgcolor: dataset.status === 'ACTIVE' ? 'warning.50' : 'success.50',
                          color: dataset.status === 'ACTIVE' ? 'warning.main' : 'success.main'
                        }
                      }}
                    >
                      {dataset.status === 'ACTIVE' ? (
                        <CancelOutlinedIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete (disabled)" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          '&:hover': { bgcolor: 'error.50', color: 'error.main' }
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
